

import { Button, IconButton } from "@a2z/ui";

import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { StorePaymentModal } from "./StorePaymentModal";
import { StorePaymentsModal } from "./StorePaymentsModal";
import { StoreScheduleLedgerModal } from "./StoreScheduleLedgerModal";
import { ImpersonateButton } from "@/shared/components/ImpersonateButton";
import { invalidateAdminStoreSubscriptions } from "@/lib/query/core/invalidation";

type Props = {
  storeId: string;
  locale: string;
  isFreeTrial?: boolean;
};

export function StoreSubscriptionActionsMenu({ storeId, locale, isFreeTrial }: Props) {
  const t = useTranslations();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [isLedgerOpen, setIsLedgerOpen] = useState(false);
  const [isPaymentsOpen, setIsPaymentsOpen] = useState(false);
  const [isAddPaymentOpen, setIsAddPaymentOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={menuRef}>
      <IconButton
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        ariaLabel={t(keys.admin.storeSubscriptionsActions)}
        variant="outline"
        size="md"
        shape="round"
        className="group hover:border-[color:var(--primary-300)]"
        icon={
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-4 w-4"
            fill="currentColor"
          >
            <circle cx="6" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="18" cy="12" r="2" />
          </svg>
        }
      />

      {isOpen ? (
        <div className="absolute right-0 top-full z-20 mt-2 w-48 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-2 text-xs shadow-[var(--shadow-sm)]">
          <div className="grid gap-2">
            <ImpersonateButton
              storeId={storeId}
              locale={locale}
              variant="ghost"
              size="sm"
              hideError
              className="w-full justify-start text-left text-xs font-semibold text-[color:var(--text-secondary)]"
              onActionStart={() => setIsOpen(false)}
            />
            <Button
              type="button"
              variant="ghost"
              className="w-full text-left text-xs font-semibold text-[color:var(--text-secondary)]"
              onClick={() => {
                setIsOpen(false);
                setIsLedgerOpen(true);
              }}
            >
              {t(keys.admin.storeSubscriptionsScheduleLedgerAction)}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-left text-xs font-semibold text-[color:var(--text-secondary)]"
              onClick={() => {
                setIsOpen(false);
                setIsPaymentsOpen(true);
              }}
            >
              {t(keys.admin.storeSubscriptionsPaymentsAction)}
            </Button>
            {isFreeTrial ? null : (
              <Button
                type="button"
                variant="ghost"
                className="w-full text-left text-xs font-semibold text-[color:var(--text-secondary)]"
                onClick={() => {
                  setIsOpen(false);
                  setIsAddPaymentOpen(true);
                }}
              >
                {t(keys.admin.subscriptionPaymentAddAction)}
              </Button>
            )}
          </div>
        </div>
      ) : null}

      <StoreScheduleLedgerModal
        storeId={storeId}
        locale={locale}
        isOpen={isLedgerOpen}
        onOpenChange={setIsLedgerOpen}
        hideTrigger
      />
      <StorePaymentsModal
        storeId={storeId}
        locale={locale}
        isOpen={isPaymentsOpen}
        onOpenChange={setIsPaymentsOpen}
        hideTrigger
      />
      {isFreeTrial ? null : (
        <StorePaymentModal
          storeId={storeId}
          isOpen={isAddPaymentOpen}
          onOpenChange={setIsAddPaymentOpen}
          hideTrigger
          onSuccess={() => {
            void invalidateAdminStoreSubscriptions(queryClient, storeId);
          }}
        />
      )}
    </div>
  );
}
